import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Stethoscope, Upload } from 'lucide-react';
import NivaLogo from '../../components/NivaLogo';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROUTES } from '../../constants/routes';
import { SPECIALTIES } from '../constants/doctorPrompts';
import { doctorSignup, doctorUpdateMe } from '../services/doctorApi';
import { compressClinicLogoFile } from '../utils/compressClinicLogo';
import { useDoctorSession } from '../contexts/DoctorSessionContext';

const inputClass =
  'w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const CLINIC_STEP_KEY = 'doctor_signup_clinic_step';

export default function DoctorSignupScreen() {
  const navigate = useNavigate();
  const { isAuthenticated, accountType, loginWithToken } = useAuth();
  const { refreshDoctor } = useDoctorSession();
  const { t } = useLanguage();
  const [step, setStep] = useState(() => {
    try {
      return sessionStorage.getItem(CLINIC_STEP_KEY) === '1' ? 2 : 1;
    } catch {
      return 1;
    }
  });
  const [slideDir, setSlideDir] = useState('forward');
  const [animKey, setAnimKey] = useState(0);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    specialty: 'General Practice',
    clinic_name: '',
    clinic_logo_url: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const clearClinicStepFlag = () => {
    try {
      sessionStorage.removeItem(CLINIC_STEP_KEY);
    } catch {
      // ignore
    }
  };

  const markClinicStep = () => {
    try {
      sessionStorage.setItem(CLINIC_STEP_KEY, '1');
    } catch {
      // ignore
    }
  };

  // Already signed in and not mid clinic-profile step → go to app
  useEffect(() => {
    const midClinic =
      step === 2 ||
      (() => {
        try {
          return sessionStorage.getItem(CLINIC_STEP_KEY) === '1';
        } catch {
          return false;
        }
      })();
    if (isAuthenticated && accountType === 'doctor' && !midClinic && step === 1) {
      navigate(ROUTES.DOCTOR_PATIENTS, { replace: true });
    }
  }, [accountType, isAuthenticated, navigate, step]);

  const goStep2 = () => {
    markClinicStep();
    setSlideDir('forward');
    setAnimKey((k) => k + 1);
    setStep(2);
  };

  const finishToApp = async () => {
    clearClinicStepFlag();
    try {
      await refreshDoctor();
    } catch {
      // enter app anyway
    }
    navigate(ROUTES.DOCTOR_PATIENTS, { replace: true });
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await doctorSignup({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        country: 'Jordan',
      });
      // Keep step-2 unlocked before auth flips (otherwise route would bounce to patients)
      markClinicStep();
      loginWithToken(data.token, data.doctor?.full_name || form.full_name, {
        accountType: 'doctor',
        termsAccepted: true,
        mustChangePassword: false,
      });
      try {
        await refreshDoctor();
      } catch {
        // continue to step 2
      }
      goStep2();
    } catch (err) {
      clearClinicStepFlag();
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    setError('');
    try {
      const dataUrl = await compressClinicLogoFile(file);
      setForm((f) => ({ ...f, clinic_logo_url: dataUrl }));
    } catch (err) {
      setError(err.message || t('doctorLogoUploadFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await doctorUpdateMe({
        specialty: form.specialty || null,
        clinic_name: form.clinic_name.trim() || null,
        clinic_logo_url: form.clinic_logo_url || null,
      });
      try {
        await refreshDoctor();
      } catch {
        // enter app anyway
      }
      finishToApp();
    } catch (err) {
      setError(err.message || 'Could not save clinic details');
    } finally {
      setLoading(false);
    }
  };

  const slideClass =
    slideDir === 'forward'
      ? 'animate-[doctorSignupIn_280ms_ease-out]'
      : 'animate-[doctorSignupBack_280ms_ease-out]';

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-sky-50 via-white to-primary-light px-4 py-8">
      <style>{`
        @keyframes doctorSignupIn {
          from { opacity: 0; transform: translateX(28px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes doctorSignupBack {
          from { opacity: 0; transform: translateX(-28px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div className="relative w-full max-w-[420px] overflow-hidden rounded-2xl bg-surface p-8 shadow-card">
        <NivaLogo className="mb-5" />

        <div className="mb-5 flex items-center gap-2">
          <div
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              step >= 1 ? 'bg-primary' : 'bg-gray-200'
            }`}
          />
          <div
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              step >= 2 ? 'bg-primary' : 'bg-gray-200'
            }`}
          />
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
            {error}
          </div>
        )}

        <div key={`${step}-${animKey}`} className={slideClass}>
          {step === 1 ? (
            <>
              <h1 className="mb-6 text-center text-2xl font-bold text-slate-900">
                {t('doctorSignupStep1Title')}
              </h1>
              <form onSubmit={handleStep1} className="space-y-3">
                <input
                  required
                  autoFocus
                  value={form.full_name}
                  onChange={set('full_name')}
                  placeholder={t('doctorFullName')}
                  className={inputClass}
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="Email"
                  className={inputClass}
                  dir="ltr"
                />
                <input
                  required
                  type="password"
                  minLength={8}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="Password"
                  className={inputClass}
                  dir="ltr"
                />
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                  {loading ? '…' : t('doctorSignupContinue')}
                </button>
              </form>
              <p className="mt-4 text-center text-sm text-gray-500">
                <Link to={ROUTES.DOCTOR_LOGIN} className="font-medium text-primary">
                  {t('doctorHaveAccount')}
                </Link>
              </p>
              <p className="mt-2 text-center text-sm text-gray-400">
                <Link to={ROUTES.LOGIN}>{t('doctorBackToEntry')}</Link>
              </p>
            </>
          ) : (
            <>
              <h1 className="mb-1 text-center text-2xl font-bold text-slate-900">
                {t('doctorSignupStep2Title')}
              </h1>
              <p className="mb-6 text-center text-sm text-gray-500">
                {t('doctorSignupStep2Body')}
              </p>
              <form onSubmit={handleStep2} className="space-y-3">
                <label className="block">
                  <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                    <Stethoscope className="h-3.5 w-3.5" />
                    {t('doctorSpecialty')}
                  </span>
                  <select
                    value={form.specialty}
                    onChange={set('specialty')}
                    className={inputClass}
                  >
                    {SPECIALTIES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                    <Building2 className="h-3.5 w-3.5" />
                    {t('doctorClinicName')}
                  </span>
                  <input
                    value={form.clinic_name}
                    onChange={set('clinic_name')}
                    placeholder={t('doctorClinicNamePlaceholder')}
                    className={inputClass}
                  />
                </label>

                <div>
                  <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                    <Upload className="h-3.5 w-3.5" />
                    {t('doctorClinicLogo')}
                  </span>
                  <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3">
                    {form.clinic_logo_url ? (
                      <img
                        src={form.clinic_logo_url}
                        alt=""
                        className="h-14 w-14 rounded-lg object-contain bg-white ring-1 ring-gray-100"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-xs text-gray-400 ring-1 ring-gray-100">
                        Logo
                      </div>
                    )}
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <label className="inline-flex cursor-pointer rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white">
                        {logoBusy ? '…' : t('doctorUploadLogo')}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={logoBusy || loading}
                          onChange={handleLogo}
                        />
                      </label>
                      {form.clinic_logo_url ? (
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, clinic_logo_url: '' }))}
                          className="block text-xs font-medium text-gray-500"
                        >
                          {t('doctorRemoveLogo')}
                        </button>
                      ) : (
                        <p className="text-[11px] text-gray-400">{t('doctorLogoHint')}</p>
                      )}
                    </div>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                  {loading ? '…' : t('doctorSignupFinish')}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={finishToApp}
                  className="w-full py-2.5 text-sm font-semibold text-gray-500"
                >
                  {t('doctorSignupSkip')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
